import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Alert, AlertTitle, Box, Typography } from '@mui/material';
import React, { ReactNode, useEffect, useState } from 'react';
import { ArrayField, AutocompleteArrayInput, BooleanInput, Datagrid, FormDataConsumer, NumberField, NumberInput, ReferenceArrayInput, ReferenceField, SimpleShowLayout, TextField, required, useDataProvider, useGetList, usePermissions, useTranslate } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { FuelScoringAccordion } from '../../../components/FuelScoringAccordion';
import { PERMISSIONS } from '../../../constants';
import { getChainId } from '../../../lib';
import { filterToQuery } from '../../../utils';

const FuelScoringsOverwriteAlert: React.FC<{ stationIds: string[], selectsAllPlaces: boolean }> = React.memo(({ stationIds, selectsAllPlaces }) => {
  const commonFilters = { isDefault: true, isEnabled: true, chainId: getChainId() };
  const { data: scoringsByStations } = useGetList('scorings',
    {
      pagination: { page: 1, perPage: 9999 },
      sort: { field: 'createdAt', order: 'DESC' },
      filter: selectsAllPlaces ? commonFilters : { placeIds: stationIds, ...commonFilters }
    }
  );

  if (!scoringsByStations || !Object.keys(scoringsByStations).length) { return <></>; }

  const formatScorings = () => {
    return Object.values(
      Object.values(scoringsByStations).reduce((acc: any, scoring: any) => {
        scoring.placeScorings?.forEach((placeScoring: any) => {
          acc[placeScoring.placeId] = {
            id: placeScoring.placeId,
            identify: scoring.identify,
            fuelScorings: scoring.fuelScorings,
          };
        });
        return acc;
      }, {})
    );
  };

  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <AlertTitle>Um ou mais postos selecionados possuem <strong>pontuação padrão</strong> vinculada</AlertTitle>
      Se você continuar, os postos abaixo serão desvinculados de suas respectivas pontuações e incorporados à nova pontuação.

      {formatScorings().map((scoring: any, index: number) => (
        <Accordion sx={{ marginTop: 2 }} key={index}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <ReferenceField source="id" record={scoring} reference="stations" label="" link={false}>
              <TextField source="name" />
            </ReferenceField>
          </AccordionSummary>
          <AccordionDetails>
            <SimpleShowLayout record={scoring} sx={{ p: 0 }}>
              <TextField source="identify" label="Código da pontuação" />
              <ArrayField source="fuelScorings" label="Fatores">
                <Datagrid bulkActionButtons={false}>
                  <ReferenceField source="fuelId" reference="fuels" label="Combustível" link={false}>
                    <TextField source="name" />
                  </ReferenceField>
                  <NumberField source="factor" label="Fator (pts/litro)" />
                </Datagrid>
              </ArrayField>
            </SimpleShowLayout>
          </AccordionDetails>
        </Accordion>
      ))}
    </Alert>
  );
});

const FormFuelScoring: React.FC<{ fuels: any[], formData?: any }> = ({ fuels, formData }) => {
  const chainId = getChainId();
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const { permissions } = usePermissions();
  const { setValue } = useFormContext();
  const [filterFuels, setFilterFuels] = useState<string[]>([]);

  useEffect(() => {
    if ((formData?.stationIds && formData.stationIds.length > 0) || formData?.selectsAllPlaces) {
      const filter = formData.selectsAllPlaces
        ? { chainId: getChainId() }
        : formData.stationIds?.length > 0
          ? { placeIds: formData.stationIds }
          : {};

      dataProvider.getList('fuels', { filter, sort: { field: 'name', order: 'ASC' }, pagination: { page: 1, perPage: 9999 } })
        .then(({ data }) => {
          const fuelIds = data.map((fuel) => fuel.id) as string[];
          setFilterFuels(fuelIds);
          setValue('availableFuelIds', fuelIds);
        });
    }
  }, [formData?.stationIds, formData?.selectsAllPlaces, dataProvider, setValue]);

  return (
    <>
      {(!formData?.chainId && (permissions && permissions.includes(PERMISSIONS.ADMIN))) && (
        <Typography>* Selecione uma rede na barra de ferramentas acima</Typography>
      )}

      <Box style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
        {!formData?.selectsAllPlaces && (
          <ReferenceArrayInput
            sort={{ field: 'name', order: 'ASC' }}
            perPage={9999}
            source="stationIds"
            reference="stations"
            isRequired
            validate={[required()]}
          >
            <AutocompleteArrayInput
              filterToQuery={filterToQuery}
              label="Postos"
              optionText="name"
              sx={{ width: 300, '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': { minWidth: 50 } }}
              noOptionsText={translate('ra.input.autocomplete.no_options_text')}
            />
          </ReferenceArrayInput>
        )}
        <BooleanInput source="selectsAllPlaces" label="Selecionar todos os postos" />
      </Box>

      {(formData?.type === 'default' && ((formData?.stationIds && formData.stationIds.length) || formData?.selectsAllPlaces)) && (
        <FuelScoringsOverwriteAlert stationIds={formData.stationIds} selectsAllPlaces={formData.selectsAllPlaces} />
      )}

      {formData?.type === 'special' && (
        <BooleanInput
          source="overlapsStandardScoring"
          label="Sobrepor a pontuação padrão"
          defaultValue={false}
        />
      )}

      {formData?.type === 'special' && (
        <Box display={{ xs: 'block', sm: 'flex' }} gap={2}>
          <NumberInput source="usageLimit" label="Limite de uso" min={1} sx={{ width: 200 }} />
          <NumberInput
            source="intervalInHours"
            label="Intervalo de uso (horas)"
            min={1}
            sx={{ width: 200 }}
          />
        </Box>
      )}

      {(chainId && fuels && filterFuels.length > 0) && (
        <FormDataConsumer>
          {({ formData: { fuelScorings } }) => {
            const arrayFuelScorings: ReactNode[] = [];
            if (fuelScorings) {
              for (let index = 0; index < fuelScorings.length; index++) {
                if (filterFuels.includes(fuelScorings[index].id)) {
                  arrayFuelScorings.push(
                    <FuelScoringAccordion
                      key={index}
                      name={fuelScorings[index].name}
                      source={`fuelScorings[${index}]`}
                    />
                  );
                }
              }
            }
            return arrayFuelScorings;
          }}
        </FormDataConsumer>
      )}
    </>
  );
};

export default React.memo(FormFuelScoring);
